// public/v3/src/lib/schedule-picker-model.js
// Pure model for the shared appointment slot picker (components/slot-picker.jsx),
// used by the patient intake wizard and by Zephyr's reschedule dialog.
// Framework-free CommonJS so Jest can require it (this repo has no JSX/ESM
// transform); the JSX imports it through esbuild's CJS interop.
//
// Two rules every function here obeys:
//   • Every date or time a person reads is rendered in the PRACTICE timezone,
//     passed in explicitly as `tz` — never the device's. Calendar days are
//     plain 'YYYY-MM-DD' strings in that zone, and day arithmetic is done on
//     the calendar (Date.UTC), so DST never shifts a day.
//   • `now` and the viewer's zone are ARGUMENTS, not reads of Date.now() or
//     Intl defaults, so every branch is reachable from a test.
//
// Availability payload consumed (both the public wizard endpoint and the staff
// endpoint return this shape):
//   { timezone, days: [{ date: 'YYYY-MM-DD', slots: [{ start, end }] }],
//     unavailable: boolean, message: string|null }
// `start`/`end` are UTC ISO instants; `date` is the practice-local day.

/* ── calendar days ('YYYY-MM-DD' in the practice zone) ───────────────── */

const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

function parseYmd(ymd) {
  const m = YMD_RE.exec(String(ymd || ''));
  if (!m) return null;
  const y = Number(m[1]); const mo = Number(m[2]); const d = Number(m[3]);
  const check = new Date(Date.UTC(y, mo - 1, d));
  if (check.getUTCFullYear() !== y || check.getUTCMonth() !== mo - 1 || check.getUTCDate() !== d) return null;
  return { y, mo, d };
}

function pad2(n) { return String(n).padStart(2, '0'); }

function toYmd(y, mo, d) { return `${y}-${pad2(mo)}-${pad2(d)}`; }

// A UTC Date at midnight of the calendar day — only ever used to FORMAT the
// day (with timeZone:'UTC') or to count days; never as an instant.
function ymdToUtcDate(ymd) {
  const p = parseYmd(ymd);
  if (!p) throw new Error(`bad calendar day: ${ymd}`);
  return new Date(Date.UTC(p.y, p.mo - 1, p.d));
}

function addDays(ymd, n) {
  const d = ymdToUtcDate(ymd);
  d.setUTCDate(d.getUTCDate() + (Number(n) || 0));
  return toYmd(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
}

// b − a in whole days.
function diffDays(a, b) {
  return Math.round((ymdToUtcDate(b) - ymdToUtcDate(a)) / 86400000);
}

// 'YYYY-MM-DD' strings order lexicographically, so plain comparison is correct.
function compareYmd(a, b) { return a < b ? -1 : a > b ? 1 : 0; }
function minYmd(a, b) { return compareYmd(a, b) <= 0 ? a : b; }
function maxYmd(a, b) { return compareYmd(a, b) >= 0 ? a : b; }

function weekWindow(startYmd, n = 7) {
  const out = [];
  for (let i = 0; i < n; i++) out.push(addDays(startYmd, i));
  return out;
}

/* ── Intl helpers (formatters cached per zone; no data in them) ───────── */

const _fmtCache = new Map();
function fmt(tz, opts) {
  const key = `${tz}|${JSON.stringify(opts)}`;
  let f = _fmtCache.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', { timeZone: tz, ...opts });
    _fmtCache.set(key, f);
  }
  return f;
}

function part(parts, type) {
  const p = parts.find((x) => x.type === type);
  return p ? p.value : '';
}

function toDate(x) {
  if (x instanceof Date) return x;
  if (typeof x === 'number') return new Date(x);
  return new Date(Date.parse(String(x)));
}

// Newer ICU puts a narrow no-break space before AM/PM; a person reads a space.
function plainSpaces(s) { return String(s).replace(/[  ]/g, ' '); }

// The practice-local calendar day an instant falls on.
function ymdInTz(instant, tz) {
  const parts = fmt(tz, { year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(toDate(instant));
  return `${part(parts, 'year')}-${part(parts, 'month')}-${part(parts, 'day')}`;
}

function todayYmd(now, tz) { return ymdInTz(now, tz); }

// '9:15 AM' — the wall-clock time of an instant in the practice zone.
function formatSlotTime(instant, tz) {
  return plainSpaces(fmt(tz, { hour: 'numeric', minute: '2-digit' }).format(toDate(instant)));
}

// Pieces of a calendar day for the strip: { dow:'Tue', day:15, mon:'Sep', month:9, year:2026 }.
function dayParts(ymd) {
  const p = parseYmd(ymd);
  if (!p) throw new Error(`bad calendar day: ${ymd}`);
  const parts = fmt('UTC', { weekday: 'short', month: 'short', day: 'numeric' }).formatToParts(ymdToUtcDate(ymd));
  return { dow: part(parts, 'weekday'), day: p.d, mon: part(parts, 'month'), month: p.mo, year: p.y };
}

// 'Tue 9/15'
function formatDayShort(ymd) {
  const p = dayParts(ymd);
  return `${p.dow} ${p.month}/${p.day}`;
}

// 'Tue, Sep 15'
function formatDayMedium(ymd) {
  const p = dayParts(ymd);
  return `${p.dow}, ${p.mon} ${p.day}`;
}

// 'Tuesday, September 15'
function formatDayLong(ymd) {
  return fmt('UTC', { weekday: 'long', month: 'long', day: 'numeric' }).format(ymdToUtcDate(ymd));
}

// 'September 2026'
function formatMonthYear(ymd) {
  return fmt('UTC', { month: 'long', year: 'numeric' }).format(ymdToUtcDate(ymd));
}

/* ── timezone labels ─────────────────────────────────────────────────── */

function tzNameParts(tz, now) {
  const d = toDate(now == null ? Date.now() : now);
  const read = (timeZoneName) => part(fmt(tz, { timeZoneName }).formatToParts(d), 'timeZoneName');
  let long = '';
  let short = '';
  try { long = read('longGeneric'); short = read('shortGeneric'); } catch (_) { /* older engine */ }
  if (!long) {
    // Fallback for engines without the generic names: strip the DST half.
    try { long = read('long').replace(/\b(Daylight|Standard|Summer)\s+/i, ''); } catch (_) { long = ''; }
  }
  if (!short) {
    try {
      const s = read('short');
      // 'EDT' → 'ET', 'AKDT' → 'AKT'; anything else is used as-is.
      if (/^[A-Z][DS]T$/.test(s)) short = s[0] + 'T';
      else if (/^[A-Z]{2}[DS]T$/.test(s)) short = s.slice(0, 2) + 'T';
      else short = s;
    } catch (_) { short = ''; }
  }
  return { long, short };
}

// 'Eastern Time (ET)'. When the short form is not a real abbreviation (London
// answers 'United Kingdom Time' twice; offsets come back as 'GMT+9') the long
// name stands alone. An unknown zone string is returned as-is rather than
// throwing — a label must never take the picker down.
function tzLabel(tz, now) {
  if (!tz) return '';
  try {
    const { long, short } = tzNameParts(tz, now);
    if (!long) return String(tz);
    const usable = short && short !== long && short.length <= 5 && !/GMT|UTC|\d/.test(short);
    return usable ? `${long} (${short})` : long;
  } catch (_) {
    return String(tz);
  }
}

// 'ET' — the suffix for a single time ("9:15 AM ET"); falls back to the long name.
function tzShort(tz, now) {
  if (!tz) return '';
  try {
    const { long, short } = tzNameParts(tz, now);
    const usable = short && short !== long && short.length <= 5 && !/GMT|UTC|\d/.test(short);
    return usable ? short : (long || String(tz));
  } catch (_) {
    return String(tz);
  }
}

// UTC offset (minutes, +east) of `tz` at an instant — render the instant as a
// wall clock in tz, read that back as UTC, diff. Correct across DST.
function offsetMinutes(instant, tz) {
  const d = toDate(instant);
  const parts = fmt(tz, {
    hour12: false, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(d);
  const n = (t) => Number(part(parts, t));
  const hour = n('hour') === 24 ? 0 : n('hour');
  const asUtc = Date.UTC(n('year'), n('month') - 1, n('day'), hour, n('minute'), n('second'));
  return Math.round((asUtc - Math.floor(d.getTime() / 1000) * 1000) / 60000);
}

// Does the viewer's device clock disagree with the practice's? Compared as
// offsets at `now`, so two names for the same zone (New York / Detroit) do not
// nag, and a real difference (a patient in Los Angeles) does.
function tzMismatch(practiceTz, viewerTz, now) {
  if (!practiceTz || !viewerTz || practiceTz === viewerTz) return false;
  try {
    return offsetMinutes(now, practiceTz) !== offsetMinutes(now, viewerTz);
  } catch (_) {
    return false;
  }
}

// The device zone, read once by the component; a failure reads as "unknown",
// which tzMismatch treats as no mismatch.
function resolveViewerTz() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch (_) { return ''; }
}

/* ── availability payload → slots by day ─────────────────────────────── */

// Sorted, de-duplicated slots keyed by practice-local day, with anything that
// has already started dropped. Days the server listed with no slots are kept
// as empty arrays so "loaded and empty" is not confused with "not loaded".
function indexSlots(payload, { now } = {}) {
  const nowMs = now == null ? Date.now() : toDate(now).getTime();
  const byYmd = {};
  const days = payload && Array.isArray(payload.days) ? payload.days : [];
  for (const day of days) {
    if (!day || !parseYmd(day.date)) continue;
    const seen = new Set();
    const slots = [];
    for (const s of (Array.isArray(day.slots) ? day.slots : [])) {
      if (!s || typeof s.start !== 'string' || typeof s.end !== 'string') continue;
      const startMs = Date.parse(s.start);
      if (!Number.isFinite(startMs) || startMs <= nowMs) continue;
      if (seen.has(s.start)) continue;
      seen.add(s.start);
      slots.push({ start: s.start, end: s.end });
    }
    slots.sort((a, b) => Date.parse(a.start) - Date.parse(b.start));
    byYmd[day.date] = (byYmd[day.date] || []).concat(slots);
  }
  return {
    byYmd,
    unavailable: !!(payload && payload.unavailable),
    message: (payload && typeof payload.message === 'string' && payload.message) || null,
    timezone: (payload && payload.timezone) || null,
  };
}

/* ── picker state machine ────────────────────────────────────────────── */

const INITIAL_FETCH_DAYS = 14;   // first load: today + 13
const PAGE_FETCH_DAYS = 14;      // each further chunk (the server caps to−from at 14)
const LOOK_AHEAD_DAYS = 28;      // an empty week looks 4 weeks past its end, once
const WEEK = 7;

function clampYmd(ymd, lo, hi) { return maxYmd(lo, minYmd(ymd, hi)); }

function initialPickerState({ now, tz, visitTypes, lockedVisitTypeId, initialSelection, maxAdvanceDays, taken } = {}) {
  if (!tz) throw new Error('initialPickerState needs the practice timezone');
  const nowMs = now == null ? Date.now() : toDate(now).getTime();
  const today = todayYmd(nowMs, tz);
  const advance = Number.isFinite(Number(maxAdvanceDays)) && Number(maxAdvanceDays) > 0 ? Math.floor(Number(maxAdvanceDays)) : 60;
  const lastYmd = addDays(today, advance);
  const types = Array.isArray(visitTypes) ? visitTypes : [];
  const sel = initialSelection && typeof initialSelection === 'object' ? initialSelection : null;
  const visitTypeId = lockedVisitTypeId
    || (sel && sel.visitTypeId)
    || (types.length === 1 ? types[0].id : null);
  const selYmd = sel && sel.start && Number.isFinite(Date.parse(sel.start)) ? ymdInTz(sel.start, tz) : null;
  const weekStart = selYmd ? clampYmd(selYmd, today, lastYmd) : today;
  const takenMap = {};
  for (const t of (Array.isArray(taken) ? taken : [])) if (typeof t === 'string') takenMap[t] = true;
  return {
    tz,
    now: nowMs,
    today,
    lastYmd,             // the last bookable calendar day (today + maxAdvanceDays)
    visitTypeId,
    weekStart,           // first day of the visible 7-day strip
    selectedYmd: selYmd, // the day whose times are listed — always on the visible strip
    // A taken initial selection anchors the strip on its week but is never the
    // pick (Fable F1): a refused slot handed back by the host must not be pressable.
    selectedStart: selYmd && !takenMap[sel.start] ? sel.start : null,
    selectedEnd: selYmd && !takenMap[sel.start] ? (sel.end || null) : null,
    byYmd: {},           // loaded slots keyed by day
    loadedTo: null,      // last calendar day covered by a completed load (contiguous from `today`)
    status: 'idle',      // idle | loading | ready | error | unavailable
    error: null,
    unavailableMessage: null,
    taken: takenMap,     // start ISO → true (greyed, not selectable)
    lookAheadUntil: null,// the one look-ahead's target; survives a failed chunk so Retry resumes it; cleared when reached
    lookedAhead: false,  // the one look-ahead has been spent for this visit type
    seq: 0,              // request sequence; a stale response is ignored
  };
}

function resetLoaded(state) {
  return {
    ...state,
    byYmd: {}, loadedTo: null, status: 'idle', error: null, unavailableMessage: null,
    lookAheadUntil: null, lookedAhead: false,
  };
}

function liveSlots(state, ymd) {
  const slots = state.byYmd[ymd];
  if (!slots) return [];
  return slots.filter((s) => Date.parse(s.start) > state.now);
}

function openCount(state, ymd) {
  return liveSlots(state, ymd).filter((s) => !state.taken[s.start]).length;
}

function isLoaded(state, ymd) {
  return state.loadedTo != null && compareYmd(ymd, state.loadedTo) <= 0 && compareYmd(ymd, state.today) >= 0;
}

function firstOpenDay(state, days) {
  for (const ymd of days) if (isLoaded(state, ymd) && openCount(state, ymd) > 0) return ymd;
  return null;
}

function visibleWindow(state) { return weekWindow(state.weekStart, WEEK); }

// The day whose times are listed always sits on the visible strip. The
// pick (selectedStart) is independent: paging away keeps it, paging back
// lists its day again.
function landOnStrip(next) {
  const visible = visibleWindow(next);
  if (next.selectedYmd && visible.includes(next.selectedYmd)) {
    if (!next.selectedStart && isLoaded(next, next.selectedYmd) && openCount(next, next.selectedYmd) === 0) {
      const first = firstOpenDay(next, visible);
      return first ? { ...next, selectedYmd: first } : next;
    }
    return next;
  }
  const pickYmd = next.selectedStart ? ymdInTz(next.selectedStart, next.tz) : null;
  if (pickYmd && visible.includes(pickYmd)) return { ...next, selectedYmd: pickYmd };
  const first = firstOpenDay(next, visible);
  return next.selectedYmd === (first || null) ? next : { ...next, selectedYmd: first || null };
}

// Empty week: look ahead once, four weeks past the visible strip, but never
// beyond the last bookable day. Runs after every load AND every page turn, so
// paging into an already-loaded empty week arms it too; a reached target is
// cleared. `loadedTo` must be known before an empty strip can mean anything.
function armLookAhead(next) {
  const weekEnd = visibleWindow(next)[WEEK - 1];
  // Over as soon as an opening past the strip is known, or the target is
  // loaded — the person is told "Next available", not made to wait for
  // the rest of the 28 days. Never started when the answer is already on
  // screen (and then not spent, so a later empty week still gets its one).
  const answered = nextAvailableDate(next) != null;
  if (next.lookAheadUntil && next.loadedTo && (answered || compareYmd(next.loadedTo, next.lookAheadUntil) >= 0)) next = { ...next, lookAheadUntil: null };
  if (!next.lookedAhead && !answered && next.loadedTo && weekIsEmpty(next)) {
    const until = minYmd(addDays(weekEnd, LOOK_AHEAD_DAYS), next.lastYmd);
    next = compareYmd(until, next.loadedTo) > 0 ? { ...next, lookAheadUntil: until, lookedAhead: true } : { ...next, lookedAhead: true };
  }
  return next;
}

function pickerReducer(state, action) {
  switch (action.type) {
    case 'SET_VISIT_TYPE': {
      if (action.visitTypeId === state.visitTypeId) return state;
      // Slots depend on the type's duration: everything loaded is stale, and
      // a slot picked for another type is no longer a pick.
      return {
        ...resetLoaded(state),
        visitTypeId: action.visitTypeId || null,
        weekStart: state.today,
        selectedYmd: null, selectedStart: null, selectedEnd: null,
        seq: state.seq + 1,
      };
    }
    case 'LOAD_START': {
      return { ...state, status: 'loading', error: null, seq: action.seq != null ? action.seq : state.seq };
    }
    case 'LOAD_OK': {
      if (action.seq != null && action.seq !== state.seq) return state; // stale
      const indexed = indexSlots(action.payload, { now: action.now != null ? action.now : state.now });
      const byYmd = { ...state.byYmd };
      // Every day in the fetched range is now known: absent from the payload
      // means no openings that day.
      if (action.from && action.to && parseYmd(action.from) && parseYmd(action.to)) {
        for (const ymd of weekWindow(action.from, diffDays(action.from, action.to) + 1)) {
          if (!byYmd[ymd]) byYmd[ymd] = [];
        }
      }
      for (const ymd of Object.keys(indexed.byYmd)) byYmd[ymd] = indexed.byYmd[ymd];
      const loadedTo = action.to && parseYmd(action.to)
        ? (state.loadedTo ? maxYmd(state.loadedTo, action.to) : action.to)
        : state.loadedTo;
      let next = {
        ...state,
        byYmd,
        loadedTo,
        now: action.now != null ? toDate(action.now).getTime() : state.now,
        status: indexed.unavailable ? 'unavailable' : 'ready',
        error: null,
        unavailableMessage: indexed.unavailable ? (indexed.message || 'Online scheduling is temporarily unavailable.') : null,
      };
      if (indexed.unavailable) return next;
      return armLookAhead(landOnStrip(next));
    }
    case 'LOAD_FAIL': {
      if (action.seq != null && action.seq !== state.seq) return state;
      // A loaded strip stays on screen; only the error line changes. The
      // look-ahead target survives so Retry resumes it.
      return { ...state, status: 'error', error: action.message || "Couldn't load times." };
    }
    case 'RETRY': {
      return { ...state, status: 'idle', error: null, seq: state.seq + 1 };
    }
    case 'PAGE': {
      const dir = action.dir > 0 ? 1 : -1;
      if (dir > 0 && !canPageNext(state)) return state;
      if (dir < 0 && !canPagePrev(state)) return state;
      const weekStart = clampYmd(addDays(state.weekStart, dir * WEEK), state.today, state.lastYmd);
      return armLookAhead(landOnStrip({ ...state, weekStart }));
    }
    case 'JUMP_TO': {
      if (!parseYmd(action.ymd)) return state;
      const weekStart = clampYmd(action.ymd, state.today, state.lastYmd);
      return { ...state, weekStart, selectedYmd: weekStart };
    }
    case 'SELECT_DAY': {
      if (!parseYmd(action.ymd)) return state;
      return { ...state, selectedYmd: action.ymd };
    }
    case 'SELECT_SLOT': {
      const slot = action.slot;
      if (!slot || typeof slot.start !== 'string') return state;
      if (state.taken[slot.start]) return state;
      if (Date.parse(slot.start) <= state.now) return state;
      return { ...state, selectedStart: slot.start, selectedEnd: slot.end || null, selectedYmd: ymdInTz(slot.start, state.tz) };
    }
    case 'CLEAR_SELECTION': {
      return { ...state, selectedStart: null, selectedEnd: null };
    }
    case 'SLOT_TAKEN': {
      if (typeof action.start !== 'string') return state;
      const taken = { ...state.taken, [action.start]: true };
      const lost = state.selectedStart === action.start;
      return { ...state, taken, selectedStart: lost ? null : state.selectedStart, selectedEnd: lost ? null : state.selectedEnd };
    }
    case 'SET_TAKEN': {
      const taken = {};
      for (const t of (Array.isArray(action.starts) ? action.starts : [])) if (typeof t === 'string') taken[t] = true;
      const lost = state.selectedStart && taken[state.selectedStart];
      return { ...state, taken, selectedStart: lost ? null : state.selectedStart, selectedEnd: lost ? null : state.selectedEnd };
    }
    case 'TICK': {
      const nowMs = action.now != null ? toDate(action.now).getTime() : Date.now();
      let next = { ...state, now: nowMs };
      const today = todayYmd(nowMs, state.tz);
      if (today !== state.today) {
        // Midnight passed under an open picker: the strip may no longer start
        // today, and yesterday's slots are gone. Keep it simple — re-anchor.
        next = { ...next, today, lastYmd: addDays(today, diffDays(state.today, state.lastYmd)), weekStart: maxYmd(state.weekStart, today) };
      }
      if (next.selectedStart && Date.parse(next.selectedStart) <= nowMs) {
        next = { ...next, selectedStart: null, selectedEnd: null };
      }
      return next;
    }
    default:
      return state;
  }
}

/* ── selectors ───────────────────────────────────────────────────────── */

function canPagePrev(state) { return compareYmd(state.weekStart, state.today) > 0; }
function canPageNext(state) { return compareYmd(addDays(state.weekStart, WEEK), state.lastYmd) <= 0; }

// The seven days on the strip.
function visibleDays(state) {
  return visibleWindow(state).map((ymd) => {
    const loaded = isLoaded(state, ymd);
    const count = loaded ? openCount(state, ymd) : 0;
    const beyond = compareYmd(ymd, state.lastYmd) > 0;
    return {
      ymd,
      parts: dayParts(ymd),
      loaded,
      count,
      isToday: ymd === state.today,
      selected: ymd === state.selectedYmd,
      disabled: beyond || (loaded && count === 0),
      beyond,
    };
  });
}

// The times listed for the selected day; taken slots stay visible, greyed.
function slotsForSelected(state) {
  if (!state.selectedYmd) return [];
  return liveSlots(state, state.selectedYmd).map((s) => ({
    start: s.start,
    end: s.end,
    label: formatSlotTime(s.start, state.tz),
    taken: !!state.taken[s.start],
    selected: s.start === state.selectedStart,
  }));
}

// Every visible day is loaded and none has an opening.
function weekIsEmpty(state) {
  const days = visibleWindow(state);
  return days.every((ymd) => isLoaded(state, ymd) || compareYmd(ymd, state.lastYmd) > 0)
    && days.every((ymd) => openCount(state, ymd) === 0);
}

// The first day with openings after the visible strip, among loaded days.
function nextAvailableDate(state) {
  if (state.loadedTo == null) return null;
  const after = addDays(state.weekStart, WEEK);
  if (compareYmd(after, state.loadedTo) > 0) return null;
  for (const ymd of weekWindow(after, diffDays(after, state.loadedTo) + 1)) {
    if (compareYmd(ymd, state.lastYmd) > 0) break;
    if (openCount(state, ymd) > 0) return ymd;
  }
  return null;
}

// What to fetch next, or null when the strip (and any pending look-ahead) is
// covered. Loads are contiguous from today: the first is 14 days, each later
// one 14 more, always clipped to the last bookable day.
function rangeToFetch(state) {
  if (!state.visitTypeId) return null;
  const visible = visibleWindow(state);
  let needTo = minYmd(visible[WEEK - 1], state.lastYmd);
  if (state.lookAheadUntil) needTo = maxYmd(needTo, minYmd(state.lookAheadUntil, state.lastYmd));
  if (state.loadedTo == null) {
    return { from: state.today, to: minYmd(addDays(state.today, INITIAL_FETCH_DAYS - 1), state.lastYmd), visitTypeId: state.visitTypeId };
  }
  if (compareYmd(state.loadedTo, needTo) >= 0) return null;
  const from = addDays(state.loadedTo, 1);
  if (compareYmd(from, state.lastYmd) > 0) return null;
  return { from, to: minYmd(addDays(from, PAGE_FETCH_DAYS - 1), state.lastYmd), visitTypeId: state.visitTypeId };
}

// 'September 2026' or 'Sep – Oct 2026' / 'Dec 2026 – Jan 2027' for a strip
// that spans a month boundary.
function weekLabel(state) {
  const days = visibleWindow(state);
  const a = dayParts(days[0]); const b = dayParts(days[WEEK - 1]);
  if (a.month === b.month && a.year === b.year) return formatMonthYear(days[0]);
  if (a.year === b.year) return `${a.mon} – ${b.mon} ${a.year}`;
  return `${a.mon} ${a.year} – ${b.mon} ${b.year}`;
}

/* ── words for the live region ───────────────────────────────────────── */

function daySummaryText(ymd, count) {
  if (!count) return `No times available on ${formatDayLong(ymd)}.`;
  return `${count} ${count === 1 ? 'time' : 'times'} available on ${formatDayLong(ymd)}.`;
}

function weekEmptyText(nextYmd) {
  return nextYmd
    ? `No openings this week. Next available: ${formatDayMedium(nextYmd)}.`
    : 'No openings this week.';
}

/* ── holds ───────────────────────────────────────────────────────────── */

function holdSecondsLeft(expiresAt, now) {
  const end = Date.parse(String(expiresAt || ''));
  if (!Number.isFinite(end)) return 0;
  const nowMs = now == null ? Date.now() : toDate(now).getTime();
  return Math.max(0, Math.ceil((end - nowMs) / 1000));
}

// 299 → '4:59'; 3661 → '1:01:01'.
function formatCountdown(totalSeconds) {
  const s = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}:${pad2(m)}:${pad2(sec)}`;
  return `${m}:${pad2(sec)}`;
}

/* ── appointment wording ─────────────────────────────────────────────── */

function modeLabel(mode, { lower } = {}) {
  const l = mode === 'video' ? 'Video' : 'In person';
  return lower ? l.toLowerCase() : l;
}

// 'Tuesday, September 15 at 9:15 AM ET (in person, 30 min)' — with the visit
// type name in front when asked: 'New Patient Visit — Tuesday, …'.
function describeAppointment(appt, tz, { withName } = {}) {
  if (!appt || !appt.start || !Number.isFinite(Date.parse(appt.start))) return '';
  const ymd = ymdInTz(appt.start, tz);
  const details = [];
  if (appt.mode) details.push(modeLabel(appt.mode, { lower: true }));
  if (Number.isFinite(Number(appt.durationMin)) && Number(appt.durationMin) > 0) details.push(`${Number(appt.durationMin)} min`);
  const when = `${formatDayLong(ymd)} at ${formatSlotTime(appt.start, tz)} ${tzShort(tz, appt.start)}`.trim();
  const text = details.length ? `${when} (${details.join(', ')})` : when;
  return withName && appt.visitTypeName ? `${appt.visitTypeName} — ${text}` : text;
}

// 'Tue 9/15 · 9:15 AM' — the queue-row chip.
function appointmentChipText(start, tz) {
  if (!start || !Number.isFinite(Date.parse(start))) return '';
  return `${formatDayShort(ymdInTz(start, tz))} · ${formatSlotTime(start, tz)}`;
}

module.exports = {
  // calendar
  parseYmd, addDays, diffDays, compareYmd, weekWindow,
  ymdInTz, todayYmd,
  // formatting
  formatSlotTime, dayParts, formatDayShort, formatDayMedium, formatDayLong, formatMonthYear,
  tzLabel, tzShort, offsetMinutes, tzMismatch, resolveViewerTz,
  // availability
  indexSlots,
  // state machine
  initialPickerState, pickerReducer,
  visibleDays, slotsForSelected, weekIsEmpty, nextAvailableDate, rangeToFetch,
  canPagePrev, canPageNext, weekLabel,
  daySummaryText, weekEmptyText,
  INITIAL_FETCH_DAYS, PAGE_FETCH_DAYS, LOOK_AHEAD_DAYS,
  // holds + wording
  holdSecondsLeft, formatCountdown,
  modeLabel, describeAppointment, appointmentChipText,
};
